import React, { useState, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Link } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { AuthContext } from '../../../../AuthContext';
import './CreateProject.css';

import { createNewProject } from '../../../../apis/projects.org';
import { fetchUserOrganisations } from '../../../../apis/organisations.api';
import { getProjUsers } from '../../../../apis/projects.org';
import { getUserRoles } from '../../../../apis/users.api';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

// Custom Popper component
const CustomPopper = styled(Popper)(({ theme }) => ({
  width: 'auto !important',
  minWidth: '45%',
  zIndex: theme.zIndex.modal,  
}));

function CreateProject({ 
  openCreateProject, 
  setOpenCreateProject, 
  setIsToastOpen, 
  setToastMessage, 
  setLoading,
  setErrorCode 
}) {
  const queryClient = useQueryClient();
  const { authData } = useContext(AuthContext);
  const { access_token, token_type } = authData;
  const [orgId, setOrgId] = useState(null);
  const [formValues, setFormValues] = useState({
    name: "",
    users: [{ user_id: null, role_id: null }],
  });

  const handleOrganizationChange = (e, value) => {
    setOrgId(value?.org_id);
  };

  const handleAddNewUser = () => {
    setFormValues({ ...formValues, users: [...formValues.users, { user_id: null, role_id: null }] });
  };

  const handleChange = (index, field, value) => {
    const newUsers = [...formValues.users];
    newUsers[index][field] = value;
    setFormValues((prevValues) => ({
      ...prevValues,
      users: newUsers
    }));
  };

  const handleRemoveUser = (e, index) => {
    const newUsers = formValues?.users.filter((user, i) => i !== index);
    setFormValues({ ...formValues, users: newUsers.length === 0 ? [{ user_id: null, role_id: null }] : newUsers });
  };

  const { data: orgsData, isLoading: loadingOrg } = useQuery({
      queryKey : ['organisations', { access_token, token_type }], 
      queryFn : fetchUserOrganisations, 
      enabled: !!access_token
    }
  );

  const { data: getUsers, isLoading: loadingUsers } = useQuery({
    queryKey : ['ProjUsers', { orgId, access_token, token_type }],
    queryFn : getProjUsers,
    enabled: !!access_token && !!orgId 
  }
  );

  const { data: getUserProjRoles, isLoading: loadingUserProjRoles } = useQuery({
    queryKey : ['userProjRoles', {role_type:"project_role", access_token, token_type}],
    queryFn : getUserRoles,
    enabled: !!access_token }
  );

  const mutation = useMutation({
    mutationFn : createNewProject,
    onSuccess: (res) => {
      queryClient.invalidateQueries('projects');
      setToastMessage("Project has been created successfully");
      setErrorCode(res?.status);
      setLoading(false);
      setIsToastOpen(true);
    },
    onError: (error) => {
      setLoading(false);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setErrorCode(error?.response?.status);
      setToastMessage(`Error: ${errorMessage}`);
      setIsToastOpen(true);
    }
  });

  const createProject = async () => {
    setLoading(true);
    setOpenCreateProject(false);
    mutation.mutate({ proj: formValues, access_token, token_type, orgId });
  };

  const createProjectFormSubmit = (e) => {
    e.preventDefault();
    createProject();
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={openCreateProject}
      onClose={() => setOpenCreateProject(false)}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
      className="createProjectModal"
    >
      <Fade in={openCreateProject}>
        <Box className="createProjectModalClass">
          <div className="createProjectModalHeader">
            <Typography
              id="transition-modal-title"
              variant="h6"
              component="h2"
              className="createProjectModalTitle"
            >
              Create Project
            </Typography>
            <span
              className="createProjectModalClose"
              onClick={() => setOpenCreateProject(false)}
            >
              &times;
            </span>
          </div>
          <form className="g-3" onSubmit={createProjectFormSubmit}>
            <div className="createProjectModalBody">
              <div className="d-flex" style={{ margin: '16px 0', position: 'relative' }}>
                <div className="col-6" style={{ paddingRight: '12px' }}>
                  <label htmlFor="project-name" className="form-label">
                    Project Name<span style={{color: 'red'}}>&#65290;</span>
                  </label>
                  <TextField
                    required
                    id="project-name"
                    placeholder="Project name"
                    sx={{ width: "100%" }}
                    onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                  />
                </div>
                <div className="col-6" style={{ paddingRight: '12px' }}>
                  <label htmlFor="org-name" className="form-label">
                    Organization Name<span style={{color: 'red'}}>&#65290;</span>
                  </label>
                  <Autocomplete
                    disablePortal
                    options={!loadingOrg ? orgsData?.filter(org => !org?.is_deleted) : []}
                    getOptionLabel={(option) => option?.name}
                    onChange={handleOrganizationChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        id="org-name"
                        placeholder="Organization name"
                        sx={{ width: "100%" }}
                      />
                    )}
                  />
                </div>
              </div>
              <div className='superAdminTitle'>Users</div>
              <div style={{ margin: '16px 0' }}>
                {formValues?.users?.map((user, index) => (
                  <div key={index} style={{ marginTop: '16px' }}>
                    <Link
                      sx={{
                        display: 'flex',
                        textDecoration: 'none',
                        justifyContent: 'flex-end',
                        marginRight: '12px',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => handleRemoveUser(e, index)}
                    >
                      Remove
                    </Link>
                    <div className="d-flex">
                      <div className="col-6" style={{ paddingRight: '12px' }}>
                        <label htmlFor="username" className="form-label">Username{index === 0 && <span style={{color: 'red'}}>&#65290;</span>}</label>
                        <Autocomplete
                          disablePortal
                          options={getUsers || []}
                          getOptionLabel={(option) => option?.name}
                          value={getUsers?.find(u => u?.user_id === user?.user_id) || null}
                          onChange={(event, newValue) => {
                            handleChange(index, 'user_id', newValue ? (newValue?.user_id) : null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              placeholder="Enter Username"
                              name="name"
                              sx={{ width: "100%" }}
                            />
                          )}
                          PopperComponent={(props) => <CustomPopper {...props} placement='bottom-start'/>}
                        />
                      </div>
                      <div className="col-6" style={{ paddingRight: '12px' }}>
                        <label htmlFor="role" className="form-label">Role{index === 0 && <span style={{color: 'red'}}>&#65290;</span>}</label>
                        <Autocomplete
                          disablePortal
                          options={getUserProjRoles || []}
                          getOptionLabel={(option) => option?.role_name}
                          value={getUserProjRoles?.find(r => r?.role_id === user?.role_id) || null}
                          onChange={(event, newValue) => {
                            handleChange(index, 'role_id', newValue ? (newValue?.role_id) : null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              placeholder="Enter role"
                              name="role"
                              sx={{ width: "100%" }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleAddNewUser}
                  sx={{
                    textTransform: 'capitalize',
                    fontFamily: 'open sans',
                    fontWeight: 400,
                    borderRadius: '0',
                    m: '16px 0'
                  }}
                >
                  Add more
                </Button>
              </div>
            </div>
            <div className="createProjectFormButtons">
              <Button variant="outlined" size="large" onClick={() => setOpenCreateProject(false)}>
                Cancel
              </Button>
              <Button variant="contained" size="large" type="submit">Create</Button>
            </div>
          </form>
        </Box>
      </Fade>
    </Modal>
  );
}

export default CreateProject;
